/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, Optional } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { ICredential, IResponse } from '../../../core/constants/interfaces';
import { HTTPClientService } from '../../../core/utils/httpclient.service';
import { UtilsService } from '../../../core/utils/utils.service';
import { ToastService } from '../../../dashboard/components/toast/toast.component';
import { AuthService } from '../../services/auth.service';

@Component({
  selector: 'app-login',
  standalone: false,
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent {
  loginForm: FormGroup;
  errorMessage: string = '';
  passwordVisible: boolean = false;
  isLoggingIn: boolean = false;
  readonly currentYear = new Date().getFullYear();

  constructor(
    private fb: FormBuilder,
    @Optional() private dialogRef: MatDialogRef<LoginComponent>,
    private http: HTTPClientService,
    private cookieService: CookieService,
    private authService: AuthService,
    private utilsService: UtilsService,
    private router: Router,
    private toastService: ToastService
  ) {
    this.loginForm = this.fb.group({
      emailAddress: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(6)]],
    });
  }

  togglePasswordVisibility(): void {
    this.passwordVisible = !this.passwordVisible;
  }

  onSubmit(): void {
    if (this.loginForm.valid) {
      const credentials: ICredential = {
        emailAddress: this.loginForm.value.emailAddress,
        password: this.loginForm.value.password,
      };

      this.isLoggingIn = true;
      this.login(credentials);
    } else {
      this.errorMessage = 'Please fill in the form correctly.';
      this.markFormGroupTouched();
    }
  }

  private login(credential: ICredential): void {
    this.http.post<IResponse>('admin/auth/login', credential).subscribe({
      next: (response: IResponse) => {
        this.isLoggingIn = false;
        this.handleLoginSuccess(response);
      },
      error: (error) => {
        this.isLoggingIn = false;
        this.handleLoginError(error);
      },
    });
  }

  private handleLoginSuccess(response: IResponse): void {
    if (response.payload) {
      if (!this.authService.hasAdminAccess(response.payload.role)) {
        this.toastService.errorToast('Access denied. Admin role required.');
        this.errorMessage = 'Access denied. Admin role required.';
        return;
      }

      const token = response.payload.authenticationResponse.token;
      const decodedToken = this.utilsService.parseJwt(token);
      const expirationDate = new Date(decodedToken.exp * 1000);
      localStorage.setItem('user', JSON.stringify(response.payload));

      this.cookieService.set(
        'auth_token',
        token,
        expirationDate,
        '/',
        undefined,
        window.location.href.includes('https'),
        'Strict'
      );

      this.cookieService.set(
        'isVerified',
        JSON.stringify(response.payload.isUserVerified),
        expirationDate,
        '/',
        undefined,
        window.location.href.includes('https'),
        'Strict'
      );

      this.toastService.successToast('Login successful! Redirecting...');
      this.handlePostLoginNavigation();
    } else {
      this.toastService.errorToast('Login failed. Please try again.');
    }
  }

  private handleLoginError(error: any): void {
    if (!(error.error || error.error.errors)) {
      this.errorMessage = 'Invalid password. Please try again.';
      this.toastService.errorToast('Invalid password. Please try again.');
      return;
    }

    this.errorMessage = error.error.errors[0].detail;
    this.toastService.errorToast(error.error.errors[0].detail);
  }

  private handlePostLoginNavigation(): void {
    const savedPath = localStorage.getItem('path');
    const redirectUrl = savedPath || '/';

    if (savedPath) {
      localStorage.removeItem('path');
    }

    setTimeout(() => {
      window.location.replace(redirectUrl);
    }, 1500);
  }

  private markFormGroupTouched(): void {
    Object.keys(this.loginForm.controls).forEach((key) => {
      const control = this.loginForm.get(key);
      control?.markAsTouched();
    });
  }

  closeDialog(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
    }
  }

  // Utility methods for template
  get emailError(): string {
    const control = this.loginForm.get('emailAddress');
    if (control?.errors && control.touched) {
      if (control.errors['required']) return 'Email is required';
      if (control.errors['email']) return 'Please enter a valid email address';
    }
    return '';
  }

  get passwordError(): string {
    const control = this.loginForm.get('password');
    if (control?.errors && control.touched) {
      if (control.errors['required']) return 'Password is required';
      if (control.errors['minlength'])
        return 'Password must be at least 6 characters';
    }
    return '';
  }

  get isFormValid(): boolean {
    return this.loginForm.valid;
  }
}
